import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Target, TrendingUp } from "lucide-react";

const CreateTip = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    sport: "",
    league: "",
    match_info: "",
    tip_type: "",
    odds: "",
    confidence: "",
    price: "",
    currency: "USD",
    is_premium: false,
    match_date: "",
    expires_at: ""
  });

  const sports = ["Football", "Basketball", "Tennis", "Baseball", "Hockey", "Cricket"];
  const currencies = ["USD", "EUR", "GBP", "BRL", "CAD"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create tips",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-tip', {
        body: formData
      });

      if (error) throw error;

      toast({
        title: "Tip created successfully!",
        description: "Your tip is now live and available to users."
      });

      navigate('/tips');
    } catch (error: any) {
      toast({
        title: "Error creating tip",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Authentication Required</h1>
          <p className="text-muted-foreground mb-8">Please sign in to create tips</p>
          <Button onClick={() => navigate('/auth')}>Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Target className="h-5 w-5 text-primary" />
              Create New Tip
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sport and League */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sport">Sport *</Label>
                  <Select value={formData.sport} onValueChange={(value) => handleInputChange('sport', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent>
                      {sports.map(sport => (
                        <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="league">League *</Label>
                  <Input
                    id="league"
                    value={formData.league}
                    onChange={(e) => handleInputChange('league', e.target.value)}
                    placeholder="e.g., Premier League"
                    required
                  />
                </div>
              </div>

              {/* Match Info */}
              <div>
                <Label htmlFor="match_info">Match Information *</Label>
                <Input
                  id="match_info"
                  value={formData.match_info}
                  onChange={(e) => handleInputChange('match_info', e.target.value)}
                  placeholder="e.g., Arsenal vs Chelsea"
                  required
                />
              </div>

              {/* Tip Type */}
              <div>
                <Label htmlFor="tip_type">Tip Type *</Label>
                <Input
                  id="tip_type"
                  value={formData.tip_type}
                  onChange={(e) => handleInputChange('tip_type', e.target.value)}
                  placeholder="e.g., Arsenal to Win, Over 2.5 Goals"
                  required
                />
              </div>

              {/* Odds and Confidence */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="odds">Odds *</Label>
                  <Input
                    id="odds"
                    type="number"
                    step="0.01"
                    value={formData.odds}
                    onChange={(e) => handleInputChange('odds', e.target.value)}
                    placeholder="2.50"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="confidence">Confidence (%)</Label>
                  <Input
                    id="confidence"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.confidence}
                    onChange={(e) => handleInputChange('confidence', e.target.value)}
                    placeholder="85"
                  />
                </div>
              </div>

              {/* Price and Currency */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Price *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="0.00 (free) or 15.00"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(currency => (
                        <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="match_date">Match Date</Label>
                  <Input
                    id="match_date"
                    type="datetime-local"
                    value={formData.match_date}
                    onChange={(e) => handleInputChange('match_date', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="expires_at">Tip Expires At</Label>
                  <Input
                    id="expires_at"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => handleInputChange('expires_at', e.target.value)}
                  />
                </div>
              </div>

              {/* Premium Toggle */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_premium"
                  checked={formData.is_premium}
                  onCheckedChange={(checked) => handleInputChange('is_premium', checked)}
                />
                <Label htmlFor="is_premium">Premium Tip</Label>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={loading}
                  variant="betting"
                >
                  {loading ? "Creating..." : "Create Tip"}
                  <TrendingUp className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateTip;